import React from 'react';
import classNames from 'classnames';
import chopAtWordBoundary from 'utils/chopAtWordBoundary';
import SelectField from 'components/pure/utils/form/SelectField';
import './SAWScorecardTabs.styl';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';

const ALL = 'ALL';
const maxLabelLength = 25;
const maxTotalLabelLength = 120;
const maxTabsToShow = 5;

export default class SAWScorecardTabs extends React.Component {
  constructor(props) {
    super(props);
    this.scorecardOptionChange = this.scorecardOptionChange.bind(this);
    this.heuristicTakeTopN = this.heuristicTakeTopN.bind(this);
    this.state = {
      selected: ALL,
      scorecardOptions: {
        scorecardOptionsTabs: undefined,
        scorecardOptionsDropdowns: undefined,
      },
    };
  }

  componentDidMount() {
    this.setUpScorecardTabs();
  }

  componentDidUpdate() {
    this.setUpScorecardTabs();
  }

  setUpScorecardTabs() {
    if (!this.state.scorecardOptions.scorecardOptionsTabs && this.props.scorecardOptions) {
      this.setState({
        scorecardOptions: this.heuristicTakeTopN(
          [{name: 'All', id: ALL}, ...this.props.scorecardOptions].map(it => ({
            value: it.id,
            label: it.name,
          }))
        ),
      });
    }
  }

  scorecardOptionChange(scorecardId) {
    const {selected, scorecardOptions} = this.state;
    const {scorecardOptionsTabs: tabs, scorecardOptionsDropdowns: dropdown} = scorecardOptions;

    if (scorecardId === selected) {
      return;
    }

    const idxInDropdown = dropdown.map(t => t.value).indexOf(scorecardId);

    // if the selected scorecard was in the dropdown, swap it with
    // the current tab (or the next tab over, if the current tab is ALL)
    if (idxInDropdown !== -1) {
      let newTabs = tabs.slice();
      const tabToSwapIdx = selected === 'ALL' ? 1 : tabs.map(t => t.value).indexOf(selected);
      const valueToSwap = tabs[tabToSwapIdx];
      newTabs[tabToSwapIdx] = dropdown.find(o => o.value === scorecardId);
      const newDropdown = [valueToSwap, ...dropdown.filter(d => d.value !== scorecardId)];

      this.setState({
        scorecardOptions: {
          scorecardOptionsTabs: newTabs,
          scorecardOptionsDropdowns: newDropdown,
        },
      });
    }

    this.setState({selected: scorecardId}, () => {
      this.props.scorecardOptionChange(scorecardId);
    });
  }

  /**
   * Given an array of scorecards (with the scorecard name in the "label" property),
   * returns two sub-arrays, one for showing as tabs, the other (the rest) for showing in the drop-down
   * @param scorecardOptions
   * @returns {{scorecardOptionsTabs: [], scorecardOptionsDropdowns: []}}
   */
  heuristicTakeTopN(scorecardOptions) {
    const len = scorecardOptions.length;
    let labelsTotalLength = 0;
    let tabs = 0;
    let i = 0;
    for (; i < len; i++) {
      const scorecard = scorecardOptions[i];
      tabs++;
      labelsTotalLength += Math.min(scorecard.label.length, maxLabelLength);
      if (tabs >= maxTabsToShow || labelsTotalLength >= maxTotalLabelLength) {
        break;
      }
    }
    if (tabs === len - 1) {
      // the drop-down will only have one scorecard, so may as well show it as tab
      tabs = len;
    }
    return {
      scorecardOptionsTabs: scorecardOptions.slice(0, tabs),
      scorecardOptionsDropdowns: scorecardOptions.slice(tabs, len),
    };
  }

  render() {
    const selectedScorecardId = this.state.selected;
    const {scorecardOptionsTabs, scorecardOptionsDropdowns} = this.state.scorecardOptions;

    if (!scorecardOptionsTabs) return null;

    const sparseTabs = scorecardOptionsTabs.length <= maxTotalLabelLength - 1 ? 'sparse-tabs' : '';
    return (
      <div className="scorecard-tabs">
        <div className="tabs">
          {scorecardOptionsTabs.map(scorecard => {
            const active = selectedScorecardId === scorecard.value ? 'active' : '';

            const choppedLabel = chopAtWordBoundary(scorecard.label, maxLabelLength);
            const needsToolTip = choppedLabel.length !== scorecard.label.length;

            return (
              <div
                key={scorecard.value}
                className={classNames('scorecard-tab', active, sparseTabs)}
                onClick={() => this.scorecardOptionChange(scorecard.value)}>
                {needsToolTip ? (
                  <GenericTooltip
                    closeImmediately
                    hoverBehavior
                    positionTop
                    tooltip={scorecard.label}>
                    {choppedLabel}
                  </GenericTooltip>
                ) : (
                  choppedLabel
                )}
              </div>
            );
          })}
        </div>
        {scorecardOptionsDropdowns.length > 0 && (
          <div className="drop-down">
            <SelectField
              placeholder="More scorecards ..."
              value={selectedScorecardId}
              tether={true}
              options={scorecardOptionsDropdowns}
              onChange={this.scorecardOptionChange}
            />
          </div>
        )}
      </div>
    );
  }
}
